import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import Cookies from "js-cookie";
import React, { useCallback, useEffect, useRef, useState } from "react";

import Icon from "@/booking/components/atoms/media/Icon";
import { ButtonActionEnum, SizeEnum } from "@/booking/enums";
import { gaEventNoHit, pushGa4Event } from "@/booking/utils/tracking";

import { useNewsLetterQuery } from "../api/useNewsLetterQuery";
import styles from "./NewsLetter.module.css";

enum Status {
  Idle = "idle",
  Form = "form",
  Submitting = "submitting",
  Success = "success",
  Fail = "fail",
}

const {
  wrapper,
  newsletterContainer,
  icon,
  content,
  heading,
  cta,
  form,
  deal,
  ctaBtn,
  learnMore,
} = styles;

const NEWSLETTER_CAMPAIGN_ID = "1259";
const NEWSLETTER_COOKIE = "hide_newsletter_signup";
const NEWSLETTER_POPOVER_COOKIE = "hide_newsletter_tip";
const COOKIE_EXPIRY_DAYS = 20 * 365;
const RECAPTCHA_SCRIPT_URL = "https://www.google.com/recaptcha/api.js";

const PARAMS = {
  CID: 785825047,
  p: 2,
  a: "r",
  SID: "",
  el: "",
  llid: "",
  counted: "",
  c: "",
  optin: "y",
};

const GTM_TRACKING = {
  CATEGORY: "All Pages",
  ACTION: "Newsletter",
  LABEL_SIGNUP: "Clicked Sign Up",
  LABEL_SUBMIT: "Clicked Submit",
} as const;

const loadReCaptchaScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve();
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${RECAPTCHA_SCRIPT_URL}"]`,
    );
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = RECAPTCHA_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });
};

const queryString = (
  formData: Record<string, string | number | boolean> = {},
): string => {
  return Object.keys(formData)
    .map((key) => `${key}=${encodeURIComponent(String(formData[key]))}`)
    .join("&");
};

interface FormProps {
  onStatusChange: (status: Status) => void;
}

const NewsLetterForm: React.FC<FormProps> = ({ onStatusChange }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaCallbackRef = useRef<(() => Promise<void>) | null>(null);

  const { triggerRecaptcha, triggerRegister } = useNewsLetterQuery();

  const handleRecaptchaCallback = useCallback(async () => {
    if (recaptchaCallbackRef.current) {
      await recaptchaCallbackRef.current();
    }
  }, []);

  useEffect(() => {
    const callbackName = `submitRecaptcha`;
    (window as any)[callbackName] = handleRecaptchaCallback;

    return () => {
      delete (window as any)[callbackName];
    };
  }, [handleRecaptchaCallback]);

  const submitWithRecaptcha = async (qs: string) => {
    try {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) {
        throw new Error("reCAPTCHA not loaded");
      }

      const captchaResponse = grecaptcha.getResponse();
      if (!captchaResponse) {
        throw new Error("reCAPTCHA response empty");
      }

      await triggerRecaptcha(captchaResponse);
      await triggerRegister(qs);

      onStatusChange(Status.Success);
    } catch (error) {
      console.error("Newsletter submission failed:", error);
      onStatusChange(Status.Fail);
    }
  };

  const validateForm = (): boolean => {
    if (!formRef.current) return false;

    const fields = Array.from(formRef.current.elements) as HTMLInputElement[];

    return fields.every((field) => {
      if (field.tagName === "BUTTON") return true;
      return field.validity.valid;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onStatusChange(Status.Submitting);

    const formData = new FormData(e.currentTarget);
    const formObject = Object.fromEntries(formData.entries()) as Record<
      string,
      string
    >;

    const qs = queryString({
      ...PARAMS,
      ...formObject,
      f: NEWSLETTER_CAMPAIGN_ID,
    });

    const grecaptcha = window.grecaptcha;

    if (!grecaptcha?.execute) {
      console.error("reCAPTCHA not available");
      onStatusChange(Status.Fail);
      return;
    }

    try {
      recaptchaCallbackRef.current = () => submitWithRecaptcha(qs);
      grecaptcha.execute();
    } catch (error) {
      console.error("reCAPTCHA execution failed:", error);
      onStatusChange(Status.Fail);
    }
  };

  return (
    <form
      ref={formRef}
      className={form}
      autoComplete="on"
      data-emarsys-form
      data-campaign-id={NEWSLETTER_CAMPAIGN_ID}
      onSubmit={handleSubmit}
      aria-label="Newsletter signup form"
      noValidate
    >
      <div>
        <input
          type="text"
          name="inp_1"
          id="inp_1"
          placeholder="First name"
          maxLength={50}
          autoComplete="given-name"
          required
          aria-required="true"
        />
        <label htmlFor="inp_1">First name</label>
      </div>
      <div>
        <input
          type="text"
          name="inp_2"
          id="inp_2"
          placeholder="Last name"
          maxLength={50}
          autoComplete="family-name"
          required
          aria-required="true"
        />
        <label htmlFor="inp_2">Last name</label>
      </div>
      <div>
        <input
          type="email"
          name="inp_3"
          id="inp_3"
          placeholder="Email"
          maxLength={254}
          autoComplete="email"
          required
          aria-required="true"
        />
        <label htmlFor="inp_3">Email</label>
      </div>

      <div>
        <button
          className={ctaBtn}
          type={ButtonActionEnum.SUBMIT}
          data-gtm-category={GTM_TRACKING.CATEGORY}
          data-gtm-action={GTM_TRACKING.ACTION}
          data-gtm-label={GTM_TRACKING.LABEL_SUBMIT}
        >
          Get the latest deals
        </button>
      </div>
    </form>
  );
};

const NewsLetter = () => {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [status, setStatus] = useState<Status>(Status.Idle);

  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const chatButtonsRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    recaptchaContainerRef.current = document.querySelector(".g-recaptcha");
    chatButtonsRef.current = document.getElementById("js-chat-buttons");
  }, []);

  const toggleRecaptchaVisibility = (show: boolean) => {
    const method = show ? "add" : "remove";

    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.classList[method]("show");
    }

    if (chatButtonsRef.current) {
      chatButtonsRef.current.classList[method]("hide");
    }
  };

  const handleShowForm = async () => {
    if (!recaptchaLoaded) {
      try {
        await loadReCaptchaScript();
        setRecaptchaLoaded(true);
      } catch (error) {
        console.error("Failed to load reCAPTCHA:", error);
        setStatus(Status.Fail);
        return;
      }
    }

    toggleRecaptchaVisibility(true);
    setStatus(Status.Form);
    gaEventNoHit("Goals", "Newsletter", "Open Form");
  };

  const hidePopover = () => {
    const popover = document.querySelector(
      `[data-popover][data-cookie=${NEWSLETTER_POPOVER_COOKIE}]`,
    );

    if (popover) {
      popover.classList.add("hide");
    }
  };

  const setCookies = () => {
    const cookieOptions = {
      expires: COOKIE_EXPIRY_DAYS,
      sameSite: "lax" as const,
    };
    Cookies.set(NEWSLETTER_COOKIE, "true", cookieOptions);
    Cookies.set(NEWSLETTER_POPOVER_COOKIE, "true", cookieOptions);
  };

  const handleStatusChange = (newStatus: Status) => {
    setStatus(newStatus);

    if (newStatus === Status.Success) {
      gaEventNoHit("Goals", "Newsletter", "Success");
      pushGa4Event({
        event: "generate_lead",
        lead_type: "Newsletter Signup",
      });
      toggleRecaptchaVisibility(false);
      hidePopover();
      setCookies();
    }
  };

  if (Cookies.get(NEWSLETTER_COOKIE) && status === Status.Idle) {
    return null;
  }

  const isFormVisible = status === Status.Form || status === Status.Submitting;

  return (
    <div className={wrapper}>
      <div className={newsletterContainer}>
        <div className={icon}>
          <Icon name={faEnvelope} size={SizeEnum.XX_LARGE} />
        </div>

        <div className={content}>
          {isFormVisible && (
            <NewsLetterForm onStatusChange={handleStatusChange} />
          )}

          {status === Status.Idle && (
            <div className={deal}>
              <div className={heading}>Never miss a deal!</div>
              <span>Sign up to receive our newsletter</span>
            </div>
          )}

          {status === Status.Success && (
            <div className={heading}>Thanks, your deals are on their way!</div>
          )}

          {status === Status.Fail && (
            <div role="alert" aria-live="assertive">
              Oops! Something went wrong, would you mind trying again?
            </div>
          )}
        </div>

        {status === Status.Idle && (
          <div className={cta}>
            <button
              className={ctaBtn}
              data-gtm-category="All Pages"
              data-gtm-action="Newsletter"
              data-gtm-label="Clicked Sign Up"
              onClick={handleShowForm}
            >
              Sign up
            </button>
          </div>
        )}
      </div>

      <div className={learnMore}>
        <a href="/privacy">
          <small>
            Learn more about how YourGolfTravel collects and uses personal data.
          </small>
        </a>
      </div>
    </div>
  );
};

export default NewsLetter;
